import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { purchaseOrdersAPI, vendorsAPI, locationsAPI, itemsAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import RequireCap from '../components/auth/RequireCap';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import { 
  ArrowLeft, 
  Save, 
  Plus, 
  Trash2, 
  Send, 
  Package,
  PackageCheck,
  FileText,
  Printer,
  Mail,
  Download
} from 'lucide-react';
import { formatDate, formatCurrency, getStatusColor } from '../lib/utils';

// Line totals must match the server to the cent. The server treats a unit cost
// as a RATE (dollars x 10^4), multiplies by the quantity, and rounds ONCE to
// cents — so doing `quantity * unit_cost` in floating point here drifts by a
// cent on 3- and 4-decimal prices (15 x 91.505 renders 1372.57 in binary float,
// 1372.58 in decimal). Mirror the server's integer maths instead.
const lineTotal = (quantity, unitCost) => {
  const rateTenThousandths = Math.round((Number(unitCost) || 0) * 10000);
  const cents = Math.round(((Number(quantity) || 0) * rateTenThousandths) / 100);
  return cents / 100;
};

export default function PODetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasRole, user: currentUser } = useAuth();
  const isNew = id === 'new';
  
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [locations, setLocations] = useState([]);
  const [items, setItems] = useState([]);
  const [showAddItem, setShowAddItem] = useState(false);
  const [showReceive, setShowReceive] = useState(false);
  // 2026-02: PO items are filtered to the selected vendor's linked catalog by default.
  // The operator can flip this toggle to show all items (useful when setting up new items).
  const [showAllItems, setShowAllItems] = useState(false);
  
  const [po, setPO] = useState({
    vendor_id: '',
    location_id: '',
    shipping_address: '',
    use_custom_address: false,
    lines: [],
    notes: '',
    expected_date: '',
    tax_rate_override: ''
  });

  // One idempotency key per opened form, generated when the form mounts and
  // sent with the create. A double-click, an impatient second click after a
  // slow response, or a browser retry all arrive carrying the SAME key, so the
  // server replays the first purchase order instead of writing a second. A
  // create that genuinely failed does not burn the key (the claim is inside the
  // rolled-back transaction), so correcting the form and saving again creates
  // one PO, as it should. Navigating away and back remounts and re-keys.
  const [createIdempotencyKey] = useState(() => (
    window.crypto?.randomUUID
      ? window.crypto.randomUUID()
      : `po-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`
  ));

  const [newLine, setNewLine] = useState({
    item_id: '',
    variant_sku: '',
    quantity: 1,
    unit_cost: 0,
    taxable: true,
    notes: ''
  });

  const [receiveData, setReceiveData] = useState([]);
  const [editing, setEditing] = useState(false);
  const [editedLines, setEditedLines] = useState([]);
  const [editTaxRate, setEditTaxRate] = useState('');
  const [taxRateEdited, setTaxRateEdited] = useState(false);

  useEffect(() => {
    loadReferenceData();
    if (!isNew) {
      loadPO();
    }
  }, [id]);

  const loadReferenceData = async () => {
    try {
      const [vendorRes, locRes, itemRes] = await Promise.all([
        vendorsAPI.list({ is_active: true }),
        locationsAPI.list({ is_active: true }),
        itemsAPI.listAll({ is_active: true })
      ]);
      setVendors(vendorRes.data);
      setLocations(locRes.data);
      setItems(itemRes.data);
    } catch (error) {
      console.error('Failed to load reference data:', error);
    }
  };

  const loadPO = async () => {
    try {
      const response = await purchaseOrdersAPI.get(id);
      setPO(response.data);
    } catch (error) {
      toast.error('Failed to load PO');
      navigate('/purchase-orders');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!po.vendor_id || po.lines.length === 0) {
      toast.error('Please select a vendor and add at least one item');
      return;
    }
    if (!po.use_custom_address && !po.location_id) {
      toast.error('Please select a delivery location or use a custom shipping address');
      return;
    }
    if (po.use_custom_address && !po.shipping_address?.trim()) {
      toast.error('Please enter a custom shipping address');
      return;
    }

    setSaving(true);
    try {
      const data = {
        vendor_id: po.vendor_id,
        location_id: po.use_custom_address ? null : po.location_id,
        shipping_address: po.use_custom_address ? po.shipping_address : (po.shipping_address || null),
        lines: po.lines.map(l => ({
          item_id: l.item_id,
          variant_sku: l.variant_sku,
          quantity: l.quantity,
          unit_cost: l.unit_cost,
          taxable: l.taxable !== false,
          notes: l.notes
        })),
        notes: po.notes || null,
        expected_date: po.expected_date || null,
        idempotency_key: createIdempotencyKey,
        tax_rate_override: (po.tax_rate_override !== '' && po.tax_rate_override != null) ? parseFloat(po.tax_rate_override) : null
      };
      
      const response = await purchaseOrdersAPI.create(data);
      toast.success('Purchase order created');
      navigate(`/purchase-orders/${response.data.id}`, { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create PO');
    } finally {
      setSaving(false);
    }
  };

  const startEditing = () => {
    setEditedLines(po.lines.map(l => ({ ...l })));
    // Seed the edit-tax-rate field with the PO's current applied rate so the user sees the
    // current value rather than an empty box. taxRateEdited stays false until the user types.
    setEditTaxRate(po.tax_rate != null ? String(po.tax_rate) : '');
    setTaxRateEdited(false);
    setEditing(true);
  };

  const handleUpdatePO = async () => {
    if (editedLines.length === 0) {
      toast.error('PO must have at least one line item');
      return;
    }
    setSaving(true);
    try {
      // Build payload. ALWAYS send the taxable flag per line and the applied tax rate so the
      // backend deterministically recomputes tax_amount = sum(taxable line totals) * rate on
      // every save — regardless of which input the user touched.
      const payload = {
        lines: editedLines.map(l => ({
          id: l.id,
          item_id: l.item_id,
          variant_sku: l.variant_sku,
          quantity: l.quantity,
          unit_cost: l.unit_cost,
          taxable: l.taxable !== false,
          quantity_received: l.quantity_received || 0,
          notes: l.notes || ''
        })),
        notes: po.notes,
        expected_date: po.expected_date,
      };
      // Only send tax_rate_override for POs that already have a tax snapshot. Legacy POs (tax_rate
      // === null) are left untaxed unless the user explicitly typed a rate.
      if (taxRateEdited) {
        const parsed = parseFloat(editTaxRate);
        payload.tax_rate_override = isNaN(parsed) ? 0 : parsed;
      } else if (po.tax_rate != null) {
        payload.tax_rate_override = po.tax_rate;
      }
      await purchaseOrdersAPI.update(id, payload);
      toast.success('PO updated');
      setEditing(false);
      setTaxRateEdited(false);
      await loadPO();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update PO');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePO = async () => {
    if (!window.confirm(`Delete ${po.po_number}? This cannot be undone.`)) return;
    try {
      await purchaseOrdersAPI.delete(id);
      toast.success('PO deleted');
      navigate('/purchase-orders');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete PO');
    }
  };

  const startReceiving = () => {
    const data = po.lines
      .filter(l => (l.quantity_received || 0) < l.quantity)
      .map(l => ({
        line_id: l.id,
        item_name: l.item_name,
        variant_sku: l.variant_sku,
        vendor_sku: l.vendor_sku || l.variant_sku,
        ordered: l.quantity,
        already_received: l.quantity_received || 0,
        remaining: l.quantity - (l.quantity_received || 0),
        quantity_received: 0,
        location_id: po.location_id,
        notes: ''
      }));
    if (data.length === 0) {
      toast.info('All items have been fully received');
      return;
    }
    setReceiveData(data);
    setShowReceive(true);
  };

  const handleReceiveItems = async () => {
    const toReceive = receiveData.filter(r => r.quantity_received > 0);
    if (toReceive.length === 0) {
      toast.error('Enter quantity for at least one item');
      return;
    }
    // Validate
    for (const r of toReceive) {
      if (r.quantity_received > r.remaining) {
        toast.error(`Cannot receive more than remaining for ${r.item_name}`);
        return;
      }
      if (!r.location_id) {
        toast.error(`Select a receiving location for ${r.item_name}`);
        return;
      }
    }
    setSaving(true);
    try {
      await purchaseOrdersAPI.receive(id, toReceive.map(r => ({
        line_id: r.line_id,
        quantity_received: r.quantity_received,
        location_id: r.location_id,
        notes: r.notes
      })));
      toast.success('Items received successfully');
      setShowReceive(false);
      loadPO();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to receive items');
    } finally {
      setSaving(false);
    }
  };

  const addLineToEdited = () => {
    const selectedItem = items.find(i => i.id === newLine.item_id);
    if (!selectedItem || !newLine.variant_sku) {
      toast.error('Select item and variant');
      return;
    }
    const variant = selectedItem.variants?.find(v => v.sku === newLine.variant_sku);
    setEditedLines([...editedLines, {
      id: `new-${Date.now()}`,
      item_id: newLine.item_id,
      item_name: selectedItem.name,
      variant_sku: newLine.variant_sku,
      vendor_sku: variant?.vendor_sku || '',
      quantity: newLine.quantity,
      unit_cost: newLine.unit_cost,
      line_total: lineTotal(newLine.quantity, newLine.unit_cost),
      taxable: newLine.taxable,
      quantity_received: 0,
      notes: newLine.notes || ''
    }]);
    setNewLine({ item_id: '', variant_sku: '', quantity: 1, unit_cost: 0, taxable: true, notes: '' });
    setShowAddItem(false);
    toast.success('Item added');
  };

  const handleAction = async (action) => {
    try {
      switch (action) {
        case 'submit':
          await purchaseOrdersAPI.submit(id);
          toast.success('PO submitted');
          break;
        case 'approve':
          await purchaseOrdersAPI.approve(id);
          toast.success('PO approved');
          break;
        case 'send':
          await purchaseOrdersAPI.send(id);
          toast.success('PO sent to vendor');
          break;
      }
      loadPO();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    }
  };

  const handleResendEmail = async () => {
    setSendingEmail(true);
    try {
      const result = await purchaseOrdersAPI.resendEmail(id);
      toast.success(result.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  const handlePrint = async () => {
    // Always print from the most recently persisted PO. Reload once to ensure tax_rate /
    // tax_amount / total_amount on screen match what the PDF will show.
    let printPO = po;
    try {
      const fresh = await purchaseOrdersAPI.get(id);
      printPO = fresh.data;
      setPO(fresh.data);
    } catch (_e) {
      // fall back to in-memory copy if reload fails
    }
    const vendor = vendors.find(v => v.id === printPO.vendor_id);
    const printWindow = window.open('', '_blank');
    // XSS-safe HTML escape for all interpolated values below
    const esc = (val) => {
      if (val === null || val === undefined) return '';
      return String(val)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    };
    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Purchase Order ${esc(printPO.po_number)}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: Arial, sans-serif; padding: 40px; color: #333; }
          .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 2px solid #001C71; }
          .logo { width: 240px; height: auto; max-height: 120px; object-fit: contain; }
          .title { color: #001C71; font-size: 28px; font-weight: bold; margin-top: 10px; }
          .po-number { color: #001C71; font-size: 24px; font-weight: bold; text-align: right; }
          .po-meta { font-size: 14px; color: #666; text-align: right; margin-top: 5px; }
          .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 40px; margin-bottom: 30px; }
          .info-section h3 { font-size: 14px; color: #666; margin-bottom: 8px; text-transform: uppercase; }
          .info-section p { font-size: 14px; margin: 4px 0; }
          .info-section .name { font-weight: bold; font-size: 16px; }
          /* Fixed layout so column widths are deterministic and long Item/SKU text wraps
             inside its own cell instead of bleeding into Qty / Unit Cost / Total. */
          table.po-items { width: 100%; table-layout: fixed; border-collapse: collapse; margin-bottom: 30px; }
          table.po-items col.col-item { width: 42%; }
          table.po-items col.col-sku  { width: 22%; }
          table.po-items col.col-qty  { width: 8%; }
          table.po-items col.col-unit { width: 13%; }
          table.po-items col.col-total{ width: 15%; }
          table.po-items th { background: #001C71; color: white; padding: 12px; text-align: left; font-size: 14px; }
          table.po-items th:nth-child(3), table.po-items th:nth-child(4), table.po-items th:nth-child(5) { text-align: right; }
          table.po-items td { padding: 12px; border-bottom: 1px solid #ddd; font-size: 14px; vertical-align: top;
                              word-break: break-word; overflow-wrap: anywhere; }
          table.po-items td:nth-child(3), table.po-items td:nth-child(4), table.po-items td:nth-child(5) { text-align: right; vertical-align: top; }
          table.po-items td.sku { font-family: monospace; font-size: 12px; }
          .total-row { background: #f5f5f5; }
          .total-row td { font-weight: bold; vertical-align: middle; }
          .total-amount { color: #001C71; font-size: 20px; }
          .notes { margin-bottom: 30px; padding: 15px; background: #f9f9f9; border-radius: 4px; }
          .notes h3 { font-size: 14px; color: #666; margin-bottom: 8px; }
          .terms { page-break-before: always; padding-top: 10px; }
          .terms h3 { color: #001C71; font-size: 16px; margin-bottom: 14px; border-bottom: 2px solid #001C71; padding-bottom: 6px; }
          .terms ul { padding-left: 20px; }
          .terms li { font-size: 13px; line-height: 1.55; margin-bottom: 8px; color: #1f2937; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div>
            <img src="/tricorps-po-logo.png" alt="TriCorps Security" class="logo" />
            <div class="title">Purchase Order</div>
          </div>
          <div>
            <div class="po-number">${esc(printPO.po_number)}</div>
            <div class="po-meta">Date: ${esc(formatDate(printPO.created_at))}</div>
            <div class="po-meta">Status: ${esc(printPO.status?.replace('_', ' ')?.toUpperCase())}</div>
          </div>
        </div>
        
        <div class="info-grid">
          <div class="info-section">
            <h3>Vendor</h3>
            <p class="name">${esc(printPO.vendor_name)}</p>
            ${vendor?.address ? `<p>${esc(vendor.address)}</p>` : ''}
            ${vendor?.contact_name ? `<p>${esc(vendor.contact_name)}</p>` : ''}
            ${vendor?.contact_email ? `<p>${esc(vendor.contact_email)}</p>` : ''}
            ${vendor?.contact_phone ? `<p>${esc(vendor.contact_phone)}</p>` : ''}
          </div>
          <div class="info-section">
            <h3>Ship To</h3>
            ${printPO.location_name ? `<p class="name">${esc(printPO.location_name)}</p>` : '<p class="name">Custom Address</p>'}
            ${printPO.shipping_address ? `<p>${esc(printPO.shipping_address)}</p>` : (() => { const loc = locations.find(l => l.id === printPO.location_id); return loc?.address ? `<p>${esc(loc.address)}</p>` : ''; })()}
            ${printPO.expected_date ? `<p>Expected: ${esc(formatDate(printPO.expected_date))}</p>` : ''}
          </div>
          <div class="info-section">
            <h3>Prepared By</h3>
            <p class="name">${esc(printPO.created_by_name || ((currentUser?.first_name || '') + ' ' + (currentUser?.last_name || '')))}</p>
            <p>${esc(printPO.created_by_email || currentUser?.email || '')}</p>
          </div>
        </div>
        
        <table class="po-items">
          <colgroup>
            <col class="col-item" />
            <col class="col-sku" />
            <col class="col-qty" />
            <col class="col-unit" />
            <col class="col-total" />
          </colgroup>
          <thead>
            <tr>
              <th>Item</th>
              <th>SKU</th>
              <th>Qty</th>
              <th>Unit Cost</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            ${printPO.lines?.map(line => {
              const lineItem = items.find(i => i.id === line.item_id);
              const lineVariant = lineItem?.variants?.find(v => v.sku === line.variant_sku);
              const variantDesc = lineVariant?.size ? ` - ${lineVariant.size}` : '';
              return `
              <tr>
                <td>${esc(line.item_name + variantDesc)}</td>
                <td class="sku">${esc(line.vendor_sku || line.variant_sku)}</td>
                <td>${esc(line.quantity)}</td>
                <td>${esc(formatCurrency(line.unit_cost))}</td>
                <td>${esc(formatCurrency(line.line_total ?? lineTotal(line.quantity, line.unit_cost)))}</td>
              </tr>
            `}).join('')}
            ${printPO.tax_rate != null ? (() => {
              const subtotal = printPO.subtotal ?? printPO.total_amount;
              const taxableSub = (printPO.lines || []).reduce((s, l) => s + ((l.taxable !== false) ? ((l.line_total != null ? l.line_total : lineTotal(l.quantity, l.unit_cost))) : 0), 0);
              const showTaxableSub = Math.abs(taxableSub - subtotal) > 0.005 && printPO.vendor_collects_tax !== false;
              return `
              <tr class="total-row">
                <td colspan="4" style="text-align: right;">Subtotal:</td>
                <td>${esc(formatCurrency(subtotal))}</td>
              </tr>
              ${showTaxableSub ? `
              <tr class="total-row">
                <td colspan="4" style="text-align: right;">Taxable Subtotal:</td>
                <td>${esc(formatCurrency(taxableSub))}</td>
              </tr>` : ''}
              ${printPO.vendor_collects_tax === false ? `
              <tr class="total-row">
                <td colspan="4" style="text-align: right;">Sales Tax:</td>
                <td>Not collected*</td>
              </tr>` : `
              <tr class="total-row">
                <td colspan="4" style="text-align: right;">Sales Tax (${esc(printPO.tax_rate)}% — ${esc(printPO.location_name || 'Ship-To')}):</td>
                <td>${esc(formatCurrency(printPO.tax_amount || 0))}</td>
              </tr>`}
              <tr class="total-row">
                <td colspan="4" style="text-align: right;">Total:</td>
                <td class="total-amount">${esc(formatCurrency(printPO.total_amount))}</td>
              </tr>`;
            })() : `
            <tr class="total-row">
              <td colspan="4" style="text-align: right;">Total:</td>
              <td class="total-amount">${esc(formatCurrency(printPO.total_amount))}</td>
            </tr>
            `}
          </tbody>
        </table>
        ${printPO.tax_rate != null && printPO.vendor_collects_tax === false ? '<p style="font-size: 12px; color: #92400e; margin-top: -20px; margin-bottom: 30px;">* Vendor does not collect sales tax — use tax may apply.</p>' : ''}
        
        ${printPO.notes ? `
          <div class="notes">
            <h3>Notes</h3>
            <p>${esc(printPO.notes)}</p>
          </div>
        ` : ''}
        
        <div class="terms">
          <h3>PURCHASE ORDER TERMS</h3>
          <ul>
            <li>Reference the PO number on all packing slips, invoices, and correspondence.</li>
            <li>Submit all invoices to AP@tricorps.com.</li>
            <li>Do not ship partial orders or substitute products without prior approval from TriCorps Security.</li>
            <li>Any changes to pricing, quantities, or delivery dates must be approved by TriCorps Security before fulfillment.</li>
            <li>Acceptance of this Purchase Order constitutes acceptance of these terms.</li>
            <li>Questions regarding this Purchase Order should be directed to the TriCorps Security representative identified on the PO or by calling 405.621.9006.</li>
          </ul>
        </div>
      </body>
      </html>
    `;
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
    };
  };

  const addLineItem = () => {
    if (!newLine.item_id || !newLine.variant_sku) {
      toast.error('Please select item and variant');
      return;
    }
    
    const item = items.find(i => i.id === newLine.item_id);
    const variant = item?.variants?.find(v => v.sku === newLine.variant_sku);
    
    setPO({
      ...po,
      lines: [...po.lines, {
        ...newLine,
        id: Date.now().toString(),
        item_name: item?.name,
        vendor_sku: variant?.vendor_sku || '',
        line_total: lineTotal(newLine.quantity, newLine.unit_cost)
      }]
    });
    
    setNewLine({ item_id: '', variant_sku: '', quantity: 1, unit_cost: 0, taxable: true, notes: '' });
    setShowAddItem(false);
  };

  const removeLine = (lineId) => {
    setPO({
      ...po,
      lines: po.lines.filter(l => l.id !== lineId)
    });
  };

  const selectedItem = items.find(i => i.id === newLine.item_id);

  // ── 2026-02: Vendor-scoped item catalog on PO creation ──
  // When a vendor is chosen and "Show all items" is off, only items whose
  // preferred_vendor_id OR vendor_links[].vendor_id matches the PO vendor are eligible.
  const itemMatchesVendor = (it, vendorId) => {
    if (!vendorId) return true;
    if (it.preferred_vendor_id === vendorId) return true;
    return (it.vendor_links || []).some(l => l.vendor_id === vendorId);
  };
  const visibleItems = useMemo(() => {
    if (!po.vendor_id || showAllItems) return items;
    return items.filter(it => itemMatchesVendor(it, po.vendor_id));
  }, [items, po.vendor_id, showAllItems]);
  const hiddenByFilter = po.vendor_id && !showAllItems ? items.length - visibleItems.length : 0;

  // Return the vendor_link for the current PO vendor on a given item (if any) — used
  // to auto-populate unit cost / vendor SKU when the operator picks an item.
  const linkForCurrentVendor = (it) => {
    if (!it || !po.vendor_id) return null;
    return (it.vendor_links || []).find(l => l.vendor_id === po.vendor_id) || null;
  };

  // ── Sales tax helpers ──
  const selectedLocation = locations.find(l => l.id === po.location_id);
  const vendorForPO = vendors.find(v => v.id === po.vendor_id);
  const vendorCollectsTax = isNew
    ? (vendorForPO?.collects_sales_tax !== false)
    : (po.vendor_collects_tax !== false);

  const computeTax = (lines) => {
    const subtotal = lines.reduce((s, l) => s + lineTotal(l.quantity, l.unit_cost), 0);
    const taxableBase = lines.reduce((s, l) => s + (l.taxable !== false ? lineTotal(l.quantity, l.unit_cost) : 0), 0);
    let rate;
    if (isNew) {
      if (po.tax_rate_override !== '' && po.tax_rate_override != null) {
        rate = parseFloat(po.tax_rate_override) || 0;
      } else if (!po.use_custom_address && selectedLocation?.tax_rate != null) {
        rate = selectedLocation.tax_rate;
      } else {
        rate = 0;
      }
    } else if (editing) {
      rate = taxRateEdited ? (parseFloat(editTaxRate) || 0) : (po.tax_rate ?? 0);
    } else {
      rate = po.tax_rate ?? 0;
    }
    const taxAmount = vendorCollectsTax ? Math.round(taxableBase * rate) / 100 : 0;
    return { subtotal, rate, taxAmount, total: subtotal + taxAmount };
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 skeleton-pulse"></div>
        <div className="h-96 skeleton-pulse rounded-lg"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="po-detail-page">
      {/* Header */}
      <div className="page-header">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/purchase-orders')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="page-title">{isNew ? 'New Purchase Order' : po.po_number}</h1>
            {!isNew && (
              <p className="text-slate-500 mt-1">
                {po.vendor_name} • Created {formatDate(po.created_at)}
              </p>
            )}
          </div>
          {!isNew && (
            <Badge className={`ml-4 ${getStatusColor(po.status)}`}>
              {po.status?.replace('_', ' ')}
            </Badge>
          )}
        </div>
        
        <div className="flex gap-3">
          {isNew && (
            <RequireCap module="purchase_orders" action="add">
              <Button onClick={handleSave} disabled={saving} data-testid="save-po-btn">
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Creating...' : 'Create PO'}
              </Button>
            </RequireCap>
          )}

          {!isNew && !editing && !['closed', 'cancelled'].includes(po.status) && (
            <RequireCap module="purchase_orders" action="edit">
              <Button variant="outline" onClick={startEditing} data-testid="edit-po-btn">
                <FileText className="w-4 h-4 mr-2" /> Edit PO
              </Button>
            </RequireCap>
          )}

          {!isNew && editing && (
            <RequireCap module="purchase_orders" action="edit">
              <>
                <Button onClick={handleUpdatePO} disabled={saving} data-testid="save-po-changes-btn">
                  <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button variant="outline" onClick={() => { setEditing(false); setEditedLines([]); }} data-testid="cancel-edit-btn">Cancel</Button>
              </>
            </RequireCap>
          )}

          {!isNew && !['closed', 'cancelled', 'received'].includes(po.status) && !editing && (
            <RequireCap module="purchase_orders" action="edit">
              <Button
                onClick={startReceiving}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="receive-po-btn"
              >
                <PackageCheck className="w-4 h-4 mr-2" />
                Receive Items
              </Button>
            </RequireCap>
          )}
          
          {!isNew && !editing && (
            <>
              <Button variant="outline" onClick={handlePrint} data-testid="print-po-btn">
                <Printer className="w-4 h-4 mr-2" /> Print / PDF
              </Button>
              <RequireCap module="purchase_orders" action="edit">
                <Button variant="outline" onClick={handleResendEmail} disabled={sendingEmail} data-testid="resend-email-btn">
                  <Mail className="w-4 h-4 mr-2" /> {sendingEmail ? 'Sending...' : 'Email to Vendor'}
                </Button>
              </RequireCap>
            </>
          )}

          {!isNew && !editing && ['draft', 'submitted'].includes(po.status) && (
            <RequireCap module="purchase_orders" action="delete">
              <Button variant="destructive" onClick={handleDeletePO} data-testid="delete-po-btn">
                <Trash2 className="w-4 h-4 mr-2" /> Delete
              </Button>
            </RequireCap>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* PO Info */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Order Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {isNew ? (
              <>
                <div className="space-y-2">
                  <Label>Vendor *</Label>
                  <Select
                    value={po.vendor_id}
                    onValueChange={(value) => setPO({ ...po, vendor_id: value })}
                  >
                    <SelectTrigger data-testid="vendor-select">
                      <SelectValue placeholder="Select vendor" />
                    </SelectTrigger>
                    <SelectContent>
                      {vendors.map(v => (
                        <SelectItem key={v.id} value={v.id}>
                          {v.name} ({v.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Custom Address Toggle */}
                <div className="flex items-center gap-2 pt-1">
                  <input
                    type="checkbox"
                    id="use-custom-address"
                    checked={po.use_custom_address}
                    onChange={(e) => setPO({ ...po, use_custom_address: e.target.checked })}
                    className="w-4 h-4 rounded border-slate-300"
                    data-testid="custom-address-checkbox"
                  />
                  <Label htmlFor="use-custom-address" className="cursor-pointer text-sm font-medium">
                    Use custom shipping address
                  </Label>
                </div>

                {!po.use_custom_address ? (
                  <div className="space-y-2">
                    <Label>Delivery Location *</Label>
                    <Select
                      value={po.location_id}
                      onValueChange={(value) => setPO({ ...po, location_id: value })}
                    >
                      <SelectTrigger data-testid="location-select">
                        <SelectValue placeholder="Select location" />
                      </SelectTrigger>
                      <SelectContent>
                        {locations.map(loc => (
                          <SelectItem key={loc.id} value={loc.id}>
                            {loc.name} ({loc.code}){loc.tax_rate != null ? ` — ${loc.tax_rate}% tax` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {selectedLocation && selectedLocation.tax_rate == null && (
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-800" data-testid="no-tax-rate-warning">
                        No tax rate on file for {selectedLocation.name} — tax will calculate at 0% unless overridden below.
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label>Shipping Address *</Label>
                    <Textarea
                      value={po.shipping_address || ''}
                      onChange={(e) => setPO({ ...po, shipping_address: e.target.value })}
                      placeholder="Enter the full shipping address..."
                      rows={3}
                      data-testid="shipping-address-input"
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Sales Tax Rate Override (%) <span className="text-slate-400 font-normal">— optional</span></Label>
                  <Input
                    type="number"
                    step="0.0001"
                    min="0"
                    value={po.tax_rate_override}
                    onChange={(e) => setPO({ ...po, tax_rate_override: e.target.value })}
                    placeholder={!po.use_custom_address && selectedLocation?.tax_rate != null ? `Location default: ${selectedLocation.tax_rate}%` : 'No location rate on file — enter a rate or tax calculates at 0%'}
                    data-testid="po-tax-override-input"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Expected Date</Label>
                    <Input
                      type="date"
                      value={po.expected_date || ''}
                      onChange={(e) => setPO({ ...po, expected_date: e.target.value })}
                      data-testid="expected-date-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea
                    value={po.notes || ''}
                    onChange={(e) => setPO({ ...po, notes: e.target.value })}
                    placeholder="Additional notes..."
                    data-testid="po-notes-input"
                  />
                </div>
              </>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-6">
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-xs uppercase font-semibold text-slate-500 mb-2">Vendor</p>
                    <p className="font-bold text-lg">{po.vendor_name}</p>
                    {(() => {
                      const vendor = vendors.find(v => v.id === po.vendor_id);
                      return vendor ? (
                        <>
                          {vendor.address && <p className="text-sm text-slate-600 mt-1">{vendor.address}</p>}
                          {vendor.contact_name && <p className="text-sm text-slate-600">{vendor.contact_name}</p>}
                          {vendor.contact_email && <p className="text-sm text-slate-600">{vendor.contact_email}</p>}
                          {vendor.contact_phone && <p className="text-sm text-slate-600">{vendor.contact_phone}</p>}
                        </>
                      ) : null;
                    })()}
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-xs uppercase font-semibold text-slate-500 mb-2">Ship To</p>
                    {po.location_name ? (
                      <p className="font-bold text-lg">{po.location_name}</p>
                    ) : (
                      <p className="font-bold text-lg">Custom Address</p>
                    )}
                    {po.shipping_address ? (
                      <p className="text-sm text-slate-600 mt-1 whitespace-pre-line">{po.shipping_address}</p>
                    ) : (() => {
                      const loc = locations.find(l => l.id === po.location_id);
                      return loc?.address ? <p className="text-sm text-slate-600 mt-1">{loc.address}</p> : null;
                    })()}
                    {po.expected_date && <p className="text-sm text-slate-600 mt-2">Expected: {formatDate(po.expected_date)}</p>}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <p className="text-slate-500 text-sm">Total Amount</p>
                    <p className="font-bold text-xl">{formatCurrency(po.total_amount)}</p>
                  </div>
                  <div>
                    <p className="text-slate-500 text-sm">Created By</p>
                    <p className="font-medium">{po.created_by_name || currentUser?.first_name + ' ' + currentUser?.last_name}</p>
                    <p className="text-sm text-slate-500">{po.created_by_email || currentUser?.email}</p>
                  </div>
                </div>
                {editing ? (
                  <div className="space-y-4 border-t pt-4">
                    <div className="space-y-2">
                      <Label>Expected Date</Label>
                      <Input
                        type="date"
                        value={po.expected_date || ''}
                        onChange={(e) => setPO({ ...po, expected_date: e.target.value })}
                        data-testid="edit-expected-date-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Notes</Label>
                      <Textarea
                        value={po.notes || ''}
                        onChange={(e) => setPO({ ...po, notes: e.target.value })}
                        placeholder="Additional notes..."
                        data-testid="edit-po-notes-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Sales Tax Rate (%)</Label>
                      <Input
                        type="number"
                        step="0.0001"
                        min="0"
                        value={editTaxRate}
                        onChange={(e) => { setEditTaxRate(e.target.value); setTaxRateEdited(true); }}
                        data-testid="edit-tax-rate-input"
                      />
                      {po.tax_location_default != null && (
                        <p className="text-xs text-slate-500">{po.location_name || 'Location'} default: {po.tax_location_default}%</p>
                      )}
                    </div>
                  </div>
                ) : (
                  po.notes && (
                    <div>
                      <p className="text-slate-500 text-sm">Notes</p>
                      <p className="font-medium">{po.notes}</p>
                    </div>
                  )
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Summary */}
        <Card>
          <CardHeader>
            <CardTitle>Summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {(() => {
              const summaryLines = editing ? editedLines : po.lines;
              const itemCount = summaryLines?.length || 0;
              const totalQty = summaryLines?.reduce((sum, l) => sum + (l.quantity || 0), 0) || 0;
              const showTax = isNew || editing || po.tax_rate != null;
              let subtotal, rate, taxAmount, total;
              if (!isNew && !editing) {
                subtotal = po.subtotal ?? po.total_amount;
                rate = po.tax_rate;
                taxAmount = po.tax_amount ?? 0;
                total = po.total_amount;
              } else {
                ({ subtotal, rate, taxAmount, total } = computeTax(summaryLines || []));
              }
              const shipToName = po.location_name || selectedLocation?.name || 'Ship-To';
              return (
                <>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Items</span>
                    <span className="font-medium">{itemCount}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Total Qty</span>
                    <span className="font-medium">{totalQty}</span>
                  </div>
                  {showTax ? (
                    <>
                      <div className="border-t pt-4 flex justify-between text-sm" data-testid="po-subtotal">
                        <span className="text-slate-500">Subtotal</span>
                        <span className="font-medium">{formatCurrency(subtotal)}</span>
                      </div>
                      {vendorCollectsTax ? (
                        <div className="flex justify-between text-sm gap-2" data-testid="po-tax-line">
                          <span className="text-slate-500 min-w-0">Sales Tax ({rate ?? 0}% — {shipToName})</span>
                          <span className="font-medium whitespace-nowrap">{formatCurrency(taxAmount)}</span>
                        </div>
                      ) : (
                        <p className="text-xs text-amber-600" data-testid="po-no-tax-msg">
                          Vendor does not collect sales tax — use tax may apply.
                        </p>
                      )}
                      {!isNew && !editing && po.tax_rate_source === 'override' && (
                        <Badge variant="outline" className="text-amber-700 border-amber-300 bg-amber-50 whitespace-normal text-left" data-testid="po-tax-override-flag">
                          Rate overridden — {po.location_name || 'location'} default {po.tax_location_default != null ? `${po.tax_location_default}%` : 'none'}
                        </Badge>
                      )}
                      <div className="border-t pt-4 flex justify-between">
                        <span className="font-medium">Total</span>
                        <span className="text-xl font-bold" data-testid="po-total">{formatCurrency(total)}</span>
                      </div>
                    </>
                  ) : (
                    <div className="border-t pt-4 flex justify-between">
                      <span className="font-medium">Total</span>
                      <span className="text-xl font-bold" data-testid="po-total">{formatCurrency(total)}</span>
                    </div>
                  )}
                </>
              );
            })()}
          </CardContent>
        </Card>
      </div>

      {/* Line Items */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Order Items</CardTitle>
              <CardDescription>Items to be ordered from vendor</CardDescription>
            </div>
            {(isNew || editing) && (
              <Dialog open={showAddItem} onOpenChange={setShowAddItem}>
                <DialogTrigger asChild>
                  <Button data-testid="add-po-item-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Item
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add Item</DialogTitle>
                    <DialogDescription>
                      {po.vendor_id
                        ? `Only items sourced from ${vendorForPO?.name || 'the selected vendor'} are shown.`
                        : 'Pick a vendor for this PO to filter the item catalog automatically.'}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    {po.vendor_id && (
                      <div className="flex items-center justify-between gap-2 p-2 rounded-md bg-slate-50 border">
                        <div className="text-xs text-slate-600">
                          {showAllItems
                            ? `Showing all ${items.length} items (vendor filter off).`
                            : `Showing ${visibleItems.length} of ${items.length} items linked to this vendor.`}
                          {hiddenByFilter > 0 && !showAllItems && (
                            <span className="ml-1 text-slate-500">{hiddenByFilter} hidden.</span>
                          )}
                        </div>
                        <label className="flex items-center gap-2 text-xs cursor-pointer whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={showAllItems}
                            onChange={(e) => setShowAllItems(e.target.checked)}
                            className="w-3.5 h-3.5 rounded border-slate-300"
                            data-testid="po-show-all-items-toggle"
                          />
                          Show all items
                        </label>
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label>Item</Label>
                      <Select
                        value={newLine.item_id}
                        onValueChange={(value) => {
                          const nextItem = items.find(i => i.id === value);
                          const link = nextItem && po.vendor_id
                            ? (nextItem.vendor_links || []).find(l => l.vendor_id === po.vendor_id)
                            : null;
                          setNewLine({
                            ...newLine,
                            item_id: value,
                            variant_sku: '',
                            // Prefer vendor-link price when we have a vendor context; else leave 0 for the operator.
                            unit_cost: (link && link.price != null) ? link.price : 0,
                          });
                        }}
                      >
                        <SelectTrigger data-testid="po-item-select">
                          <SelectValue placeholder={visibleItems.length === 0 ? 'No items available for this vendor' : 'Select item'} />
                        </SelectTrigger>
                        <SelectContent>
                          {visibleItems.map(it => {
                            const isPreferred = it.preferred_vendor_id && it.preferred_vendor_id === po.vendor_id;
                            return (
                              <SelectItem key={it.id} value={it.id}>
                                {it.name}{isPreferred ? ' ★' : ''}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                      {po.vendor_id && !showAllItems && visibleItems.length === 0 && (
                        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                          No items are linked to this vendor yet. Toggle <b>Show all items</b> above, or add
                          vendor links on the items you buy from them.
                        </p>
                      )}
                    </div>
                    {selectedItem && (
                      <div className="space-y-2">
                        <Label>Size / Variant</Label>
                        <Select
                          value={newLine.variant_sku}
                          onValueChange={(value) => {
                            const variant = selectedItem.variants?.find(v => v.sku === value);
                            // 2026-02 priority (variant-scoped vendor pricing is authoritative):
                            //   1. variant.vendor_links[matching PO vendor].price
                            //   2. item.vendor_links[matching PO vendor].price (legacy fallback)
                            //   3. variant.price
                            //   4. 0
                            const variantLink = variant && po.vendor_id
                              ? (variant.vendor_links || []).find(l => l.vendor_id === po.vendor_id)
                              : null;
                            const itemLink = linkForCurrentVendor(selectedItem);
                            let nextCost = 0;
                            if (variantLink && variantLink.price != null) nextCost = variantLink.price;
                            else if (itemLink && itemLink.price != null) nextCost = itemLink.price;
                            else if (variant?.price) nextCost = variant.price;
                            setNewLine({ ...newLine, variant_sku: value, unit_cost: nextCost });
                          }}
                        >
                          <SelectTrigger data-testid="po-variant-select">
                            <SelectValue placeholder="Select variant" />
                          </SelectTrigger>
                          <SelectContent>
                            {selectedItem.variants?.map(v => (
                              <SelectItem key={v.sku} value={v.sku}>
                                {v.size ? `${v.size} (${v.sku})` : v.sku} {v.price > 0 ? `- ${formatCurrency(v.price)}` : ''}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Quantity</Label>
                        <Input
                          type="number"
                          min="1"
                          value={newLine.quantity}
                          onChange={(e) => setNewLine({ ...newLine, quantity: parseInt(e.target.value) || 1 })}
                          data-testid="po-quantity-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Unit Cost ($)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={newLine.unit_cost}
                          onChange={(e) => setNewLine({ ...newLine, unit_cost: parseFloat(e.target.value) || 0 })}
                          data-testid="po-cost-input"
                        />
                      </div>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-sm text-slate-500">Line Total</p>
                      <p className="text-lg font-bold">
                        {formatCurrency(lineTotal(newLine.quantity, newLine.unit_cost))}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="line-taxable"
                        checked={newLine.taxable}
                        onChange={(e) => setNewLine({ ...newLine, taxable: e.target.checked })}
                        className="w-4 h-4 rounded border-slate-300"
                        data-testid="po-taxable-checkbox"
                      />
                      <Label htmlFor="line-taxable" className="cursor-pointer text-sm font-medium">Taxable</Label>
                    </div>
                    <Button onClick={editing ? addLineToEdited : addLineItem} className="w-full" data-testid="confirm-add-po-item">
                      Add to Order
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {(() => {
            const displayLines = editing ? editedLines : po.lines;
            const hasLines = displayLines?.length > 0;

            if (!hasLines) {
              return (
                <div className="text-center py-12 text-slate-500">
                  <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>No items added yet</p>
                  {(isNew || editing) && <p className="text-sm">Click "Add Item" to start adding items</p>}
                </div>
              );
            }

            return (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Item</TableHead>
                    <TableHead>Vendor SKU</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                    {!isNew && !editing && <TableHead className="text-right">Received</TableHead>}
                    <TableHead className="text-right">Unit Cost</TableHead>
                    <TableHead className="text-center">Taxable</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    {(isNew || editing) && <TableHead></TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {displayLines.map((line, index) => {
                    const lineItem = items.find(i => i.id === line.item_id);
                    const lineVariant = lineItem?.variants?.find(v => v.sku === line.variant_sku);
                    const variantDesc = lineVariant?.size ? ` - ${lineVariant.size}` : '';

                    if (editing) {
                      return (
                        <TableRow key={line.id || index} data-testid={`edit-line-${index}`}>
                          <TableCell className="font-medium">{line.item_name}{variantDesc}</TableCell>
                          <TableCell className="font-mono">{line.vendor_sku || line.variant_sku}</TableCell>
                          <TableCell className="text-right">
                            <Input
                              type="number"
                              min="1"
                              className="w-20 text-right ml-auto"
                              value={line.quantity}
                              onChange={(e) => {
                                const updated = [...editedLines];
                                updated[index] = { ...updated[index], quantity: parseInt(e.target.value) || 1 };
                                setEditedLines(updated);
                              }}
                              data-testid={`edit-qty-${index}`}
                            />
                          </TableCell>
                          <TableCell className="text-right">
                            <Input
                              type="number"
                              step="0.01"
                              className="w-24 text-right ml-auto"
                              value={line.unit_cost}
                              onChange={(e) => {
                                const updated = [...editedLines];
                                updated[index] = { ...updated[index], unit_cost: parseFloat(e.target.value) || 0 };
                                setEditedLines(updated);
                              }}
                              data-testid={`edit-cost-${index}`}
                            />
                          </TableCell>
                          <TableCell className="text-center">
                            <input
                              type="checkbox"
                              checked={line.taxable !== false}
                              onChange={(e) => {
                                const updated = [...editedLines];
                                updated[index] = { ...updated[index], taxable: e.target.checked };
                                setEditedLines(updated);
                              }}
                              className="w-4 h-4 rounded border-slate-300"
                              data-testid={`edit-taxable-${index}`}
                            />
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(lineTotal(line.quantity, line.unit_cost))}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setEditedLines(editedLines.filter((_, i) => i !== index))}
                              data-testid={`remove-edit-line-${index}`}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    }

                    if (isNew) {
                      return (
                        <TableRow key={line.id || index}>
                          <TableCell className="font-medium">{line.item_name}{variantDesc}</TableCell>
                          <TableCell className="font-mono">{line.vendor_sku || line.variant_sku}</TableCell>
                          <TableCell className="text-right">{line.quantity}</TableCell>
                          <TableCell className="text-right">{formatCurrency(line.unit_cost)}</TableCell>
                          <TableCell className="text-center">
                            <input
                              type="checkbox"
                              checked={line.taxable !== false}
                              onChange={(e) => {
                                setPO({
                                  ...po,
                                  lines: po.lines.map(l => l.id === line.id ? { ...l, taxable: e.target.checked } : l)
                                });
                              }}
                              className="w-4 h-4 rounded border-slate-300"
                              data-testid={`new-taxable-${index}`}
                            />
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(line.line_total ?? lineTotal(line.quantity, line.unit_cost))}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => removeLine(line.id)}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    }

                    return (
                      <TableRow key={line.id || index}>
                        <TableCell className="font-medium">{line.item_name}{variantDesc}</TableCell>
                        <TableCell className="font-mono">{line.vendor_sku || line.variant_sku}</TableCell>
                        <TableCell className="text-right">{line.quantity}</TableCell>
                        <TableCell className="text-right">{line.quantity_received || 0}</TableCell>
                        <TableCell className="text-right">{formatCurrency(line.unit_cost)}</TableCell>
                        <TableCell className="text-center text-slate-500" data-testid={`view-taxable-${index}`}>
                          {line.taxable !== false ? '✓' : '—'}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(line.line_total ?? lineTotal(line.quantity, line.unit_cost))}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            );
          })()}
        </CardContent>
      </Card>

      {/* Receive Dialog */}
      <Dialog open={showReceive} onOpenChange={setShowReceive}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Receive Items — {po.po_number}</DialogTitle>
            <DialogDescription>Enter quantities received and select the storage location for each item</DialogDescription>
          </DialogHeader>
          <div className="mt-4 max-h-[60vh] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Item</TableHead>
                  <TableHead className="text-right">Ordered</TableHead>
                  <TableHead className="text-right">Received</TableHead>
                  <TableHead className="text-right">Remaining</TableHead>
                  <TableHead className="text-right">Receiving Now</TableHead>
                  <TableHead>Receive To Location</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {receiveData.map((rd, index) => (
                  <TableRow key={rd.line_id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{rd.item_name}</p>
                        <p className="text-xs text-slate-500">{rd.vendor_sku}</p>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">{rd.ordered}</TableCell>
                    <TableCell className="text-right">{rd.already_received}</TableCell>
                    <TableCell className="text-right font-medium">{rd.remaining}</TableCell>
                    <TableCell className="text-right">
                      <Input
                        type="number"
                        min="0"
                        max={rd.remaining}
                        className="w-20 text-right ml-auto"
                        value={rd.quantity_received}
                        onChange={(e) => {
                          const updated = [...receiveData];
                          updated[index] = { ...updated[index], quantity_received: parseInt(e.target.value) || 0 };
                          setReceiveData(updated);
                        }}
                        data-testid={`receive-qty-${index}`}
                      />
                    </TableCell>
                    <TableCell>
                      <Select
                        value={rd.location_id}
                        onValueChange={(val) => {
                          const updated = [...receiveData];
                          updated[index] = { ...updated[index], location_id: val };
                          setReceiveData(updated);
                        }}
                      >
                        <SelectTrigger className="w-[180px]" data-testid={`receive-loc-${index}`}>
                          <SelectValue placeholder="Select location" />
                        </SelectTrigger>
                        <SelectContent>
                          {locations.map(loc => (
                            <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowReceive(false)}>Cancel</Button>
            <Button onClick={handleReceiveItems} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-receive-btn">
              <PackageCheck className="w-4 h-4 mr-2" /> {saving ? 'Processing...' : 'Confirm Receipt'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
